import { PostAdChooser } from "@/components/post-ad-chooser";
import { useMemo, useState } from "react";
import { breadcrumbScript } from "@/lib/seo-jsonld";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  Briefcase,
  FileText,
  GraduationCap,
  Mail,
  MapPin,
  MessageCircle,
  Phone,
  Plus,
  Search,
  UserSearch,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TimeAgo } from "@/components/time-ago";
import { cities, subcategoriesOf } from "@/lib/data";
import type { Ad } from "@/lib/data";
import { fetchActiveListings } from "@/lib/listings";
import { isJobSeeker } from "@/lib/specs";
import { adSlug } from "@/lib/seo";

export const Route = createFileRoute("/is-elanlari")({
  head: () => ({
    meta: [
      { title: "İş elanları və vakansiyalar — Albazar.az" },
      {
        name: "description",
        content:
          "Azərbaycanda pulsuz iş elanları: vakansiyalar və «iş axtarıram» elanları. Şəhər və sahə üzrə filtrləyin, işəgötürənlə birbaşa əlaqə saxlayın.",
      },
      { property: "og:title", content: "İş elanları və vakansiyalar — Albazar.az" },
      {
        property: "og:description",
        content: "Pulsuz vakansiya yerləşdir və ya iş axtar — birbaşa əlaqə.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://albazar.az/is-elanlari" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://albazar.az/is-elanlari" }],
    scripts: [breadcrumbScript([{ name: "İş elanları", path: "/is-elanlari" }])],
  }),
  component: JobsPage,
});

function salaryText(ad: Ad) {
  const min = ad.specs?.["salary_min"];
  const max = ad.specs?.["salary_max"];
  if (min || max) {
    if (min && max) return `${min} – ${max} ₼`;
    return `${min || max} ₼-dan`;
  }
  const custom = ad.specs?.["salary"] ?? ad.specs?.["expected_salary"];
  if (custom) return custom;
  const period = ad.specs?.["salary_period"];
  if (!ad.price) return period === "Razılaşma ilə" ? "Razılaşma ilə" : "Razılaşma ilə";
  return `${ad.price.toLocaleString("az-AZ")} ₼${period && period !== "Razılaşma ilə" ? ` / ${period.toLowerCase()}` : ""}`;
}

function JobCard({ ad }: { ad: Ad }) {
  const seeker = isJobSeeker(ad.specs);
  const chips = (
    seeker
      ? [
          ad.specs?.["age"] ? `${ad.specs["age"]} yaş` : "",
          ad.specs?.["experience"],
          ad.specs?.["education"],
          ad.specs?.["profession"],
        ]
      : [
          ad.specs?.["schedule"],
          ad.specs?.["experience_required"],
          ad.specs?.["job_role"] ?? ad.specs?.["profession"],
          ad.specs?.["address"],
        ]
  ).filter(Boolean) as string[];
  const person = seeker ? ad.specs?.["full_name_seeker"] : ad.specs?.["company"];
  const role = seeker ? ad.specs?.["desired_position"] : ad.specs?.["position"];
  const email = ad.specs?.["email"] ?? ad.specs?.["email_seeker"];
  const cv = ad.specs?.["cv_url"];
  return (
    <article className="rounded-3xl border border-border bg-card p-4 shadow-card transition-shadow hover:shadow-elevated">
      <div className="flex items-start justify-between gap-3">
        <span
          className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold ${
            seeker ? "bg-info/10 text-info" : "bg-primary/10 text-primary"
          }`}
        >
          {seeker ? <UserSearch className="size-3.5" /> : <Briefcase className="size-3.5" />}
          {seeker ? "İş axtarıram" : "Vakansiya"}
        </span>
        <span className="text-[11px] text-muted-foreground">
          <TimeAgo iso={ad.createdAt} />
        </span>
      </div>

      <Link to="/elan/$id" params={{ id: adSlug(ad) }} className="mt-2 block">
        <h2 className="line-clamp-2 text-base font-extrabold leading-snug hover:text-primary">
          {ad.title}
        </h2>
      </Link>

      {(person || role) && (
        <p className="mt-1 text-sm text-muted-foreground">
          {[role, person].filter(Boolean).join(" — ")}
        </p>
      )}

      <p className="mt-2 text-lg font-black text-teal">{salaryText(ad)}</p>

      <div className="mt-2 flex flex-wrap gap-1.5">
        <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-[11px] font-medium text-muted-foreground">
          <MapPin className="size-3" /> {ad.city}
        </span>
        {chips.map((c) => (
          <span
            key={c}
            className="rounded-full bg-muted px-2.5 py-1 text-[11px] font-medium text-muted-foreground"
          >
            {c}
          </span>
        ))}
      </div>

      {(email || cv) && (
        <div className="mt-2 flex flex-wrap gap-3 text-[11px] font-semibold">
          {email && (
            <a href={`mailto:${email}`} className="inline-flex items-center gap-1 text-primary">
              <Mail className="size-3.5" /> {email}
            </a>
          )}
          {cv && (
            <a
              href={cv}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-primary"
            >
              <FileText className="size-3.5" /> CV-yə bax
            </a>
          )}
        </div>
      )}

      <div className="mt-3 flex gap-2">
        <Link to="/elan/$id" params={{ id: adSlug(ad) }} className="flex-1">
          <Button variant="outline" className="h-10 w-full rounded-2xl text-sm font-bold">
            Ətraflı
          </Button>
        </Link>
        {ad.phone ? (
          <a href={`tel:${ad.phone}`} className="flex-1">
            <Button className="h-10 w-full rounded-2xl text-sm font-bold">
              <Phone className="size-4" /> Zəng et
            </Button>
          </a>
        ) : null}
        {ad.whatsapp ? (
          <a
            href={`https://wa.me/${ad.whatsapp.replace(/\D/g, "")}`}
            target="_blank"
            rel="noreferrer"
            aria-label="WhatsApp ilə yaz"
          >
            <Button variant="outline" className="h-10 rounded-2xl px-3">
              <MessageCircle className="size-4" />
            </Button>
          </a>
        ) : null}
      </div>
    </article>
  );
}

function JobsPage() {
  const { data } = useQuery({
    queryKey: ["listings", "active"],
    queryFn: fetchActiveListings,
    staleTime: 30_000,
  });

  const [tab, setTab] = useState<"vakansiya" | "axtaran">("vakansiya");
  const [q, setQ] = useState("");
  const [city, setCity] = useState("");

  const jobs = useMemo(
    () => (data ?? []).filter((a) => a.category === "is-elanlari" && a.status === "active"),
    [data],
  );

  const results = useMemo(() => {
    return jobs.filter((a) => {
      const seeker = isJobSeeker(a.specs);
      if (tab === "vakansiya" && seeker) return false;
      if (tab === "axtaran" && !seeker) return false;
      if (city && a.city !== city) return false;
      if (q && !`${a.title} ${a.description}`.toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
  }, [jobs, tab, city, q]);

  const fields = subcategoriesOf("is-elanlari");

  return (
    <div className="container-x py-8">
      <div className="rounded-3xl border border-border bg-card p-5 shadow-card">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div>
            <h1 className="text-2xl font-black tracking-tight md:text-3xl">İş elanları</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              İşəgötürənlər pulsuz vakansiya, iş axtaranlar isə «İş axtarıram» elanı yerləşdirə
              bilər. Elanlar admin təsdiqindən sonra dərc olunur.
            </p>
          </div>
          <PostAdChooser jobsOnly>
            <button
              type="button"
              className="inline-flex w-full shrink-0 items-center justify-center gap-2 rounded-2xl bg-info px-6 py-4 text-base font-extrabold text-white shadow-glow transition-transform hover:bg-info/90 active:scale-[0.98] md:w-auto md:text-lg"
            >
              <Plus className="size-5" strokeWidth={3} /> İş Elanı Yerləşdir
            </button>
          </PostAdChooser>
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <div className="rounded-2xl border border-border p-4">
            <p className="flex items-center gap-2 text-sm font-bold">
              <Briefcase className="size-4 text-primary" /> İşəgötürən üçün
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Şirkət adı, vəzifə, maaş, iş qrafiki, ünvan, əlaqə nömrəsi, WhatsApp, e-poçt, ətraflı
              məlumat və loqo/şəkil.
            </p>
          </div>
          <div className="rounded-2xl border border-border p-4">
            <p className="flex items-center gap-2 text-sm font-bold">
              <GraduationCap className="size-4 text-info" /> İş axtaran üçün
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Ad-soyad, yaş, şəhər, axtardığı vəzifə, təcrübə, təhsil, gözlənilən maaş, əlaqə
              məlumatları, qısa məlumat, CV və şəkil.
            </p>
          </div>
        </div>
      </div>

      <div className="mt-4 inline-flex rounded-full border border-border bg-card p-1">
        {(
          [
            ["vakansiya", "Vakansiyalar"],
            ["axtaran", "İş axtaranlar"],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={`rounded-full px-4 py-2 text-sm font-bold transition-colors ${
              tab === key ? "bg-primary text-primary-foreground" : "text-muted-foreground"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "axtaran" && (
        <div className="mt-4 flex flex-col gap-3 rounded-3xl border border-info/30 bg-info/5 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm font-bold">CV-nizi yerləşdirin</p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Ad-soyad, təcrübə, təhsil, gözlədiyiniz maaş və CV faylınızı əlavə edin —
              işəgötürənlər sizinlə birbaşa əlaqə saxlasın.
            </p>
          </div>
          <Link to="/yeni-elan" search={{ tip: "axtaran" as const }} className="shrink-0">
            <Button className="h-11 w-full rounded-2xl bg-info font-bold text-white hover:bg-info/90 sm:w-auto">
              <FileText className="size-4" /> CV / İş axtarıram elanı
            </Button>
          </Link>
        </div>
      )}

      <div className="mt-4 grid gap-3 rounded-3xl border border-border bg-card p-4 shadow-card md:grid-cols-3">
        <div className="relative md:col-span-2">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Vəzifə və ya açar söz"
            aria-label="İş axtarışı"
            className="h-11 rounded-2xl pl-9"
          />
        </div>
        <Select value={city || "hamisi"} onValueChange={(v) => setCity(v === "hamisi" ? "" : v)}>
          <SelectTrigger className="h-11! rounded-2xl">
            <SelectValue placeholder="Şəhər" />
          </SelectTrigger>
          <SelectContent className="max-h-72">
            <SelectItem value="hamisi">Bütün şəhərlər</SelectItem>
            {cities.map((c) => (
              <SelectItem key={c} value={c}>
                {c}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="mt-3 -mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
        {fields.map((f) => (
          <button
            key={f.slug}
            type="button"
            onClick={() => setQ(f.name)}
            className="shrink-0 rounded-full border border-border px-3.5 py-1.5 text-xs font-medium transition-colors hover:border-primary/50 hover:bg-accent"
          >
            {f.name}
          </button>
        ))}
      </div>

      <p className="mt-4 text-sm text-muted-foreground">{results.length} elan tapıldı</p>

      {results.length === 0 ? (
        <div className="mt-12 rounded-3xl border border-dashed border-border p-10 text-center">
          <p className="text-lg font-semibold">Hələ elan yoxdur</p>
          <p className="mt-1 text-sm text-muted-foreground">
            İlk elanı siz yerləşdirin — tamamilə pulsuzdur.
          </p>
          <Link to="/yeni-elan" search={{}}>
            {" "}
            <Button className="mt-5 rounded-full">
              <Plus className="size-4" /> Pulsuz elan yerləşdir
            </Button>
          </Link>
        </div>
      ) : (
        <div className="mt-4 grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {results.map((a) => (
            <JobCard key={a.id} ad={a} />
          ))}
        </div>
      )}
    </div>
  );
}
